export class RealVoiceService {
    private recognition: any;
    private isInitialized: boolean = false;
    private isListening: boolean = false;
    private isMobile: boolean;
    private isIOS: boolean;
    private isIOSChrome: boolean;
    private hasUserGesture: boolean = false;
    private lastUserGestureTime: number = 0;
    private iosWarmupCompleted: boolean = false;
    
    // Disable TTS globally - only use for specific word-related messages
    private disableTTS: boolean = true;

    constructor() {
        // Detect mobile devices
        this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        
        // Detect iOS Chrome specifically (which doesn't support speech recognition)
        this.isIOSChrome = this.isIOS && navigator.userAgent.includes('CriOS');
        
        const SpeechRecognitionClass = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        this.recognition = SpeechRecognitionClass ? new SpeechRecognitionClass() : null;

        if (this.recognition) {
            this.recognition.lang = 'he-IL';
            
            // iOS Safari needs different settings than other mobile browsers
            if (this.isIOS && !this.isIOSChrome) {
                // iOS Safari specific settings for better Hebrew recognition
                this.recognition.continuous = false; // Single utterance for iOS Safari
                this.recognition.interimResults = true; // Enable interim results
                this.recognition.maxAlternatives = 3; // More alternatives for better accuracy
                console.log('🍎 Configured for iOS Safari Hebrew recognition');
            } else if (this.isMobile) {
                // Android and other mobile browsers
                this.recognition.continuous = true;
                this.recognition.interimResults = true;
                this.recognition.maxAlternatives = 1;
                this.recognition.serviceURI = null;
                
                try {
                    (this.recognition as any).grammars = null;
                    (this.recognition as any).continuous = false;
                } catch (e) {
                    console.log('Some mobile-specific settings not available:', e);
                }
            }
        }
        
        // Log mobile detection for debugging
        if (this.isMobile) {
            if (this.isIOSChrome) {
                console.log('iOS Chrome detected');
                console.warn('iOS Chrome does not support Web Speech API for speech recognition');
            } else if (this.isIOS) {
                console.log('iOS Safari detected - speech recognition should work');
            } else {
                console.log('Android device detected');
            }
        }
    }


    async initialize(): Promise<void> {
        if (this.isInitialized) return;
        
        try {
            if (!window.speechSynthesis) {
                throw new Error('Speech synthesis not supported');
            }
            
            const testUtterance = new SpeechSynthesisUtterance('');
            testUtterance.volume = 0;
            speechSynthesis.speak(testUtterance);
            
            this.isInitialized = true;
            console.log('Voice service initialized successfully');
        } catch (error) {
            console.error('Failed to initialize voice service:', error);
            throw error;
        }
    }

    // Check if speech recognition is available
    isSpeechRecognitionAvailable(): boolean {
        if (this.isIOSChrome) {
            return false; // iOS Chrome doesn't support speech recognition
        }
        return !!this.recognition;
    }

    // Get mobile-specific error message
    getMobileErrorMessage(): string {
        if (this.isIOSChrome) {
            return 'Voice input is not supported in Chrome on iOS. Please use Safari or the text input.';
        } else if (this.isMobile && !this.recognition) {
            return 'Voice input is not supported on this mobile browser. Please try Safari on iOS or Chrome on Android.';
        } else if (this.isMobile && !this.hasUserGesture) {
            return 'Please tap the microphone button to enable voice input.';
        }
        return 'Voice input is not available on this device.';
    }

    async listen(): Promise<string> {
        console.log('🎯 VoiceService.listen() called');
        console.log('Device info:', { isMobile: this.isMobile, isIOS: this.isIOS, isIOSChrome: this.isIOSChrome, hasUserGesture: this.hasUserGesture });
        
        // Check for iOS Chrome specifically (Safari should work)
        if (this.isIOSChrome) {
            console.log('❌ iOS Chrome detected - speech recognition not supported');
            throw new Error('iOS Chrome does not support speech recognition');
        }

        if (!this.recognition) {
            console.log('❌ No speech recognition API available');
            throw new Error('Speech recognition not supported');
        }

        // Check for user gesture on mobile
        if (this.isMobile && !this.hasUserGesture) {
            console.log('❌ Mobile device without user gesture');
            throw new Error('User gesture required for speech recognition on mobile');
        }

        // Force stop any existing recognition before starting new one
        if (this.isListening) {
            console.log('🛑 Stopping existing recognition before starting new one');
            this.stopListening();
            // Wait a bit for the previous session to fully stop
            await new Promise(resolve => setTimeout(resolve, 200));
        }

        // iOS warm-up: Run a quick recognition session first time to improve accuracy
        if (this.isIOS && !this.iosWarmupCompleted) {
            console.log('🍎 iOS first-time warm-up: Running calibration session');
            try {
                await this.performWarmupRecognition();
                this.iosWarmupCompleted = true;
                console.log('✅ iOS warm-up completed successfully');
            } catch (error) {
                console.log('⚠️ iOS warm-up failed, continuing anyway:', error);
                this.iosWarmupCompleted = true; // Mark as completed to avoid repeated attempts
            }
        }
        
        // On mobile, recreate the recognition object to prevent accumulation
        if (this.isMobile) {
            console.log('📱 Mobile: Recreating speech recognition object to prevent accumulation');
            const SpeechRecognitionClass = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
            if (SpeechRecognitionClass) {
                this.recognition = new SpeechRecognitionClass();
                this.recognition.lang = 'he-IL';
                this.recognition.continuous = true;
                this.recognition.interimResults = true;
                this.recognition.maxAlternatives = 1;
                this.recognition.serviceURI = null;
            }
        }

        // Check and request microphone permission on mobile devices (avoid repeated popups)
        if (this.isMobile && navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
            try {
                // First, try to check if permission is already granted (if Permissions API is available)
                let permissionGranted = false;
                
                if (navigator.permissions && navigator.permissions.query) {
                    try {
                        const permissionResult = await navigator.permissions.query({ name: 'microphone' as PermissionName });
                        console.log('🎤 Current microphone permission status:', permissionResult.state);
                        
                        if (permissionResult.state === 'granted') {
                            console.log('✅ Microphone permission already granted - skipping request');
                            permissionGranted = true;
                        } else if (permissionResult.state === 'denied') {
                            throw new Error('Microphone permission denied. Please enable microphone access in browser settings.');
                        }
                        // If state is 'prompt', we'll request permission below
                    } catch (queryError) {
                        console.log('⚠️ Permission query failed, will request directly:', queryError);
                        // Fall through to direct permission request
                    }
                }
                
                // Request permission only if not already granted
                if (!permissionGranted) {
                    console.log('🎤 Requesting microphone permission on mobile...');
                    console.log('Current URL protocol:', window.location.protocol);
                    
                    // Request microphone with specific constraints for better noise rejection
                    const stream = await navigator.mediaDevices.getUserMedia({ 
                        audio: {
                            echoCancellation: true,
                            noiseSuppression: true,
                            autoGainControl: true
                        }
                    });
                    console.log('✅ Microphone permission granted with noise suppression');
                    // Stop the stream immediately since we only needed permission
                    stream.getTracks().forEach(track => track.stop());
                } else {
                    console.log('✅ Using existing microphone permission');
                }
            } catch (permissionError) {
                console.error('❌ Microphone permission denied:', permissionError);
                
                console.error('Permission error details:', {
                    name: permissionError instanceof Error ? permissionError.name : 'Unknown',
                    message: permissionError instanceof Error ? permissionError.message : 'Unknown error',
                    protocol: window.location.protocol,
                    hostname: window.location.hostname
                });
                
                // Check if it's an HTTPS issue
                if (window.location.protocol === 'http:' && window.location.hostname !== 'localhost') {
                    throw new Error('Voice input requires HTTPS. Please access the app using https:// instead of http://');
                }
                
                // Check specific error types
                if (permissionError instanceof Error) {
                    if (permissionError.name === 'NotAllowedError') {
                        throw new Error('Microphone permission denied. Please click "Allow" when Chrome asks for microphone access.');
                    } else if (permissionError.name === 'NotSupportedError') {
                        throw new Error('Microphone not supported. Please use HTTPS or try a different browser.');
                    } else if (permissionError.name === 'NotFoundError') {
                        throw new Error('No microphone found. Please check your device has a working microphone.');
                    }
                }
                
                throw new Error('Microphone access denied. Please allow microphone access and try again.');
            }
        }

        console.log('✅ All checks passed, starting speech recognition...');

        return new Promise((resolve, reject) => {
            this.isListening = true;
            let resultReceived = false;
            let speechDetected = false;
            let speechStartTime = 0;
            let speechEndTime = 0;
            let mainTimeout: NodeJS.Timeout;
            let speechEndTimeout: NodeJS.Timeout;
            let bestTranscript = ''; // Store the best result we've received

            // Main timeout - longer for mobile
            const mainTimeoutDuration = this.isMobile ? 30000 : 15000; // 30 seconds for mobile, 15 for desktop
            
            const setMainTimeout = () => {
                mainTimeout = setTimeout(() => {
                    if (this.isListening && !resultReceived) {
                        console.log(`⏰ Main speech recognition timeout after ${mainTimeoutDuration/1000} seconds`);
                        this.isListening = false;
                        this.recognition.stop();
                        reject(new Error('Speech recognition timeout - please try again'));
                    }
                }, mainTimeoutDuration);
            };
            
            setMainTimeout();

            this.recognition.onresult = (event: any) => {
                // IMPORTANT: Ignore any results after we've already processed one successfully
                if (resultReceived) {
                    console.log('🚫 Ignoring additional speech result - already processed successfully');
                    return;
                }
                
                // Get the latest result
                const results = event.results;
                const lastResult = results[results.length - 1];
                let transcript = lastResult[0].transcript;
                const isFinal = lastResult.isFinal;
                
                // iOS Safari: Check alternatives for better Hebrew accuracy
                if (this.isIOS && !this.isIOSChrome && lastResult.length > 1) {
                    console.log(`🍎 iOS Safari: ${lastResult.length} alternatives available`);
                    for (let i = 0; i < Math.min(lastResult.length, 3); i++) {
                        console.log(`  Alternative ${i}: "${lastResult[i].transcript}" (confidence: ${lastResult[i].confidence})`);
                    }
                    
                    // Find the alternative with Hebrew content
                    for (let i = 0; i < lastResult.length; i++) {
                        const alt = lastResult[i].transcript;
                        const hasHebrew = /[\u0590-\u05FF]/.test(alt);
                        if (hasHebrew) {
                            console.log(`🍎 Selecting Hebrew alternative: "${alt}"`);
                            transcript = alt;
                            break;
                        }
                    }
                }
                
                console.log(`🎤 Speech result: "${transcript}" (final: ${isFinal})`);
                
                // Store best transcript (only if non-empty)
                if (transcript.trim().length > 0) {
                    bestTranscript = transcript;
                }
                
                // Mobile: Intelligent early stopping for Hebrew words
                if (this.isMobile && transcript.trim().length > 0) {
                    const hebrewWords = transcript.match(/[\u0590-\u05FF]+/g);
                    const hasValidWord = hebrewWords && hebrewWords.length > 0 && hebrewWords[0].length >= 2;
                    
                    if (hasValidWord && !resultReceived) {
                        console.log(`📱 Mobile: Detected Hebrew word "${hebrewWords[0]}" - stopping in 2s`);
                        
                        setTimeout(() => {
                            if (this.isListening) {
                                this.recognition.stop();
                            }
                        }, 2000);
                        
                        if (isFinal) {
                            clearTimeout(mainTimeout);
                            if (speechEndTimeout) clearTimeout(speechEndTimeout);
                            resultReceived = true; // Only set when resolving
                            this.isListening = false;
                            
                            // iOS: Immediately abort to prevent further results
                            if (this.isIOS) {
                                console.log('🍎 iOS: Aborting recognition immediately after result');
                                this.recognition.abort();
                            }
                            
                            resolve(transcript);
                            return;
                        }
                    }
                }
                
                // Desktop: Process final results immediately
                if (!this.isMobile || isFinal) {
                    if (!resultReceived && transcript.trim().length > 0) {
                        console.log(`✅ Processing result: "${transcript}"`);
                        clearTimeout(mainTimeout);
                        if (speechEndTimeout) clearTimeout(speechEndTimeout);
                        resultReceived = true;
                        this.isListening = false;
                        
                        // iOS: Immediately abort to prevent further results
                        if (this.isIOS) {
                            console.log('🍎 iOS: Aborting recognition immediately after result');
                            this.recognition.abort();
                        }
                        
                        resolve(transcript);
                    }
                }
            };

            this.recognition.onerror = (event: any) => {
                console.error('❌ Speech recognition onerror triggered:', event);
                clearTimeout(mainTimeout);
                if (speechEndTimeout) clearTimeout(speechEndTimeout);
                this.isListening = false;
                console.error('Error details:', {
                    error: event.error,
                    message: event.message,
                    type: event.type,
                    timeStamp: event.timeStamp
                });
                
                // Mobile-specific error handling
                if (this.isMobile) {
                    console.log('📱 Processing mobile-specific error handling');
                    switch (event.error) {
                        case 'not-allowed':
                            console.error('🚫 Microphone permission denied on mobile');
                            reject(new Error('Microphone access denied. Please allow microphone access and try again.'));
                            break;
                        case 'no-speech':
                            console.log('🔇 No speech detected on mobile');
                            reject(new Error('no-speech'));
                            break;
                        case 'network':
                            console.error('📡 Network error on mobile');
                            reject(new Error('Network error. Please check your internet connection.'));
                            break;
                        case 'service-not-allowed':
                            console.error('🌐 Service not allowed on mobile - likely HTTPS required');
                            reject(new Error('Speech service not allowed. Please use HTTPS or try a different browser.'));
                            break;
                        case 'audio-capture':
                            console.error('🎵 Audio capture error on mobile');
                            reject(new Error('Audio capture error. Please check microphone permissions.'));
                            break;
                        case 'aborted':
                            console.error('🛑 Speech recognition aborted on mobile');
                            reject(new Error('Speech recognition was aborted.'));
                            break;
                        default:
                            console.error('❓ Unknown mobile error:', event.error);
                            reject(new Error(`Speech recognition error: ${event.error}`));
                    }
                } else {
                    if (event.error === 'no-speech') {
                        reject(new Error('no-speech'));
                    } else {
                        reject(new Error(`Speech recognition error: ${event.error}`));
                    }
                }
            };

            this.recognition.onstart = () => {
                console.log('✅ Speech recognition onstart - microphone is active');
            };

            this.recognition.onend = () => {
                console.log('🔚 Speech recognition onend triggered');
                clearTimeout(mainTimeout);
                if (speechEndTimeout) clearTimeout(speechEndTimeout);
                this.isListening = false;
                
                // If we have a stored transcript (from early stopping), use it
                if (this.isMobile && bestTranscript.trim().length > 0 && !resultReceived) {
                    // Extract just the first Hebrew word to avoid repetitions like "אמריקה אמריקה אמריקה"
                    const hebrewWords = bestTranscript.match(/[\u0590-\u05FF]+/g);
                    const cleanedTranscript = hebrewWords && hebrewWords.length > 0 ? hebrewWords[0] : bestTranscript;
                    console.log(`📱 Mobile: Using cleaned transcript from early stopping: "${cleanedTranscript}" (original: "${bestTranscript}")`);
                    resolve(cleanedTranscript);
                    return;
                }
                
                // Only reject if we haven't received a result yet and have no stored transcript
                if (!resultReceived) {
                    console.log('⚠️ Speech recognition ended without result');
                    
                    // iOS Safari specific: If speech was detected but no transcript, it might be a recognition issue
                    if (this.isIOS && !this.isIOSChrome && speechDetected) {
                        console.log('🍎 iOS Safari: Speech detected but no transcript - this might be an iOS Safari limitation');
                        reject(new Error('Speech detected but not recognized - please try speaking more clearly'));
                    } else {
                        reject(new Error('Speech recognition ended without result - please try again'));
                    }
                } else {
                    console.log('✅ Speech recognition ended normally after receiving result');
                }
            };

            this.recognition.onsoundstart = () => {
                console.log('🔊 Sound detected - microphone is picking up audio (could be ambient noise)');
            };

            this.recognition.onsoundend = () => {
                console.log('🔇 Sound ended - no more audio detected');
            };

            this.recognition.onspeechstart = () => {
                speechDetected = true;
                speechStartTime = Date.now();
                console.log('🗣️ Speech started - voice pattern detected (more confident than sound)');
                console.log(`📱 Mobile device: ${this.isMobile ? 'YES' : 'NO'} - will use enhanced end detection`);
            };

            this.recognition.onspeechend = () => {
                const speechDuration = Date.now() - speechStartTime;
                speechEndTime = Date.now();
                console.log(`🤐 Speech ended - voice pattern stopped (duration: ${speechDuration}ms)`);
                
                // If speech was very short (less than 500ms), it might be noise
                if (speechDetected && speechDuration < 500) {
                    console.log('⚠️ Very short speech duration - might be ambient noise');
                    return;
                }
                
                // On mobile, automatically stop recognition after speech ends
                if (this.isMobile && speechDetected && speechDuration > 500) {
                    console.log('📱 Mobile: Setting timeout to stop recognition after speech end');
                    speechEndTimeout = setTimeout(() => {
                        if (this.isListening && !resultReceived) {
                            console.log('📱 Mobile: Stopping recognition after speech end timeout');
                            this.recognition.stop();
                        }
                    }, 1500); // Wait 1.5 seconds after speech ends before stopping
                }
            };

            try {
                console.log('🚀 Attempting to start speech recognition...');
                this.recognition.start();
                console.log('✅ Speech recognition start() called successfully');
            } catch (error) {
                console.error('❌ Exception when starting speech recognition:', error);
                this.isListening = false;
                reject(new Error('Failed to start speech recognition'));
            }
        });
    }

    async speak(text: string): Promise<void> {
        // TTS is disabled globally - only use speakForced for specific cases
        if (this.disableTTS) {
            console.log(`🔇 TTS disabled - would have spoken: "${text}"`);
            return Promise.resolve();
        }

        console.log(`🗣️ TTS enabled - attempting to speak: "${text}"`);
        return this.speakForced(text);
    }

    // Force TTS for specific word-related messages (bypasses global disable)
    async speakForced(text: string): Promise<void> {
        console.log(`🗣️ Forced TTS - speaking: "${text}"`);

        if (!this.isInitialized) {
            console.warn('Voice service not initialized - attempting to initialize');
            try {
                await this.initialize();
            } catch (error) {
                console.error('Failed to initialize voice service for speaking:', error);
                return;
            }
        }

        return new Promise((resolve) => {
            // Enhanced mobile/tablet support with simple, direct approach
            if (this.isMobile) {
                console.log('📱 Using direct tablet TTS approach');
                
                // Check if speech synthesis is available and ready
                if (!speechSynthesis || !speechSynthesis.speak) {
                    console.error('❌ Speech synthesis not available on this mobile device');
                    resolve();
                    return;
                }

                // Always mark gesture as recent for forced TTS
                this.markUserGesture();
                console.log('📱 User gesture force-marked for tablet TTS');
                
                // Direct TTS attempt without complex retry mechanism
                this.performSimpleTabletTTS(text, resolve);
            } else {
                // Desktop TTS
                this.performTTS(text, resolve);
            }
        });
    }

    // Simple tablet TTS with minimal interference
    private performSimpleTabletTTS(text: string, resolve: () => void): void {
        console.log('📱 Simple tablet TTS for:', text);
        
        try {
            // Cancel any existing speech
            speechSynthesis.cancel();
            
            setTimeout(() => {
                const utterance = new SpeechSynthesisUtterance(text);
                utterance.lang = 'he-IL';
                utterance.rate = 0.8;
                utterance.pitch = 1.0;
                utterance.volume = 1.0;
                
                // Critical: Mobile voice selection for Hebrew
                const voices = speechSynthesis.getVoices();
                console.log(`📱 Available voices: ${voices.length}`);
                
                const hebrewVoice = voices.find(voice => 
                    voice.lang.includes('he') || voice.lang.includes('iw') || voice.name.includes('Hebrew')
                );
                
                if (hebrewVoice) {
                    console.log('📱 Using Hebrew voice:', hebrewVoice.name, hebrewVoice.lang);
                    utterance.voice = hebrewVoice;
                } else {
                    console.warn('📱 No Hebrew voice found on Android tablet!');
                    console.log('📱 Available voices:', voices.map(v => `${v.name} (${v.lang})`));
                    
                    // Try to find any Arabic voice as fallback (RTL language)
                    const arabicVoice = voices.find(voice => 
                        voice.lang.includes('ar') || voice.name.includes('Arabic')
                    );
                    
                    if (arabicVoice) {
                        console.log('📱 Using Arabic voice as Hebrew fallback:', arabicVoice.name);
                        utterance.voice = arabicVoice;
                    } else {
                        console.log('📱 Using default voice for Hebrew text (may sound wrong)');
                        // Note: Hebrew text may be spoken incorrectly with non-Hebrew voice
                    }
                }
                
                let resolved = false;
                
                utterance.onstart = () => {
                    console.log('📱 Simple TTS started');
                };
                
                utterance.onend = () => {
                    if (!resolved) {
                        resolved = true;
                        console.log('📱 Simple TTS completed');
                        resolve();
                    }
                };
                
                utterance.onerror = (event) => {
                    if (!resolved) {
                        resolved = true;
                        console.error('📱 Simple TTS error:', event.error);
                        resolve();
                    }
                };
                
                // Simple timeout - resolve after 3 seconds regardless
                setTimeout(() => {
                    if (!resolved) {
                        resolved = true;
                        console.log('📱 Simple TTS timeout - resolving');
                        resolve();
                    }
                }, 3000);
                
                console.log('📱 Speaking with simple TTS...');
                speechSynthesis.speak(utterance);
                
                // Try to ensure speech starts
                setTimeout(() => {
                    if (speechSynthesis.paused) {
                        console.log('📱 Resuming paused speech');
                        speechSynthesis.resume();
                    }
                }, 100);
                
            }, 200);
            
        } catch (error) {
            console.error('📱 Simple TTS exception:', error);
            resolve();
        }
    }

    // Tablet-specific TTS with fast timeout 
    private attemptTabletTTS(text: string, resolve: () => void, attempt: number): void {
        const maxAttempts = 2; // Reduced attempts
        
        if (attempt >= maxAttempts) {
            console.error('📱 Tablet TTS failed - resolving quickly to avoid delays');
            resolve();
            return;
        }
        
        console.log(`📱 Tablet TTS attempt ${attempt + 1}/${maxAttempts} with fast timeout`);
        
        // Cancel any existing speech
        speechSynthesis.cancel();
        
        // Minimal delay
        setTimeout(() => {
            try {
                const utterance = new SpeechSynthesisUtterance(text);
                utterance.lang = 'he-IL';
                utterance.rate = 1.0;
                utterance.pitch = 1.0;
                utterance.volume = 1.0;
                
                let completed = false;
                let timeoutId: NodeJS.Timeout;
                
                utterance.onstart = () => {
                    console.log(`📱 TTS started (attempt ${attempt + 1})`);
                };
                
                utterance.onend = () => {
                    if (!completed) {
                        completed = true;
                        clearTimeout(timeoutId);
                        console.log(`✅ Tablet TTS completed on attempt ${attempt + 1}`);
                        resolve();
                    }
                };
                
                utterance.onerror = (event) => {
                    if (!completed) {
                        completed = true;
                        clearTimeout(timeoutId);
                        console.error(`❌ TTS error on attempt ${attempt + 1}:`, event.error);
                        resolve(); // Don't retry on error, just resolve
                    }
                };
                
                // Very short timeout for tablets to prevent app flow delays
                const timeoutDuration = 1000; // Fixed 1 second timeout
                timeoutId = setTimeout(() => {
                    if (!completed) {
                        completed = true;
                        console.warn(`⏰ TTS timeout after 1s (attempt ${attempt + 1}) - resolving to prevent delays`);
                        speechSynthesis.cancel(); // Cancel the utterance
                        resolve(); // Resolve immediately instead of retrying
                    }
                }, timeoutDuration);
                
                console.log(`🎤 Starting speech synthesis with 1s timeout...`);
                speechSynthesis.speak(utterance);
                
            } catch (error) {
                console.error(`❌ Exception in TTS attempt ${attempt + 1}:`, error);
                resolve(); // Don't retry on exception, just resolve
            }
        }, 100); // Minimal delay
    }

    private performTTS(text: string, resolve: () => void): void {
        speechSynthesis.cancel();

        setTimeout(() => {
            try {
                const utterance = new SpeechSynthesisUtterance(text);
                utterance.lang = 'he-IL';
                utterance.rate = this.isMobile ? 0.9 : 0.8; // Slightly faster on mobile
                utterance.pitch = 1.1;
                utterance.volume = 1.0;
                
                // Mobile-specific voice selection
                if (this.isMobile) {
                    const voices = speechSynthesis.getVoices();
                    const hebrewVoice = voices.find(voice => 
                        voice.lang.includes('he') || voice.lang.includes('iw') || voice.name.includes('Hebrew')
                    );
                    if (hebrewVoice) {
                        console.log('📱 Using Hebrew voice:', hebrewVoice.name);
                        utterance.voice = hebrewVoice;
                    } else {
                        console.log('📱 No Hebrew voice found, using default');
                    }
                }
                
                let completed = false;
                
                utterance.onend = () => {
                    if (!completed) {
                        completed = true;
                        console.log('✅ Forced speech ended successfully');
                        resolve();
                    }
                };
                
                utterance.onerror = (event) => {
                    if (!completed) {
                        completed = true;
                        console.error('❌ Forced speech synthesis error:', event);
                        console.error('Error type:', event.error);
                        resolve();
                    }
                };

                // Mobile timeout fallback - some devices don't fire onend reliably
                if (this.isMobile) {
                    const estimatedDuration = text.length * 100; // Rough estimate
                    setTimeout(() => {
                        if (!completed) {
                            completed = true;
                            console.log('📱 Mobile TTS timeout fallback triggered');
                            resolve();
                        }
                    }, Math.max(estimatedDuration, 3000));
                }
                
                console.log('🎤 Starting speech synthesis...');
                speechSynthesis.speak(utterance);
                
                // Force start on mobile devices (workaround for some browsers)
                if (this.isMobile && speechSynthesis.paused) {
                    setTimeout(() => {
                        console.log('📱 Resuming paused speech synthesis');
                        speechSynthesis.resume();
                    }, 100);
                }
                
            } catch (error) {
                console.error('❌ Forced speech synthesis error:', error);
                resolve();
            }
        }, this.isMobile ? 500 : 300); // Longer delay for mobile
    }

    stopSpeaking(): void {
        try {
            speechSynthesis.cancel();
        } catch (error) {
            console.warn('Error stopping speech synthesis:', error);
        }
    }

    // Mark that a user gesture has occurred (needed for mobile TTS)
    markUserGesture(): void {
        this.hasUserGesture = true;
        this.lastUserGestureTime = Date.now();
        console.log('📱 User gesture marked for TTS activation');
    }

    // Check if we have a recent user gesture (within 5 seconds)
    private hasRecentUserGesture(): boolean {
        const timeSinceGesture = Date.now() - this.lastUserGestureTime;
        return this.hasUserGesture && timeSinceGesture < 5000;
    }

    // TTS-only test method - doesn't request microphone permissions
    async testTTSOnly(text: string): Promise<void> {
        console.log(`🧪 TTS-only test - speaking: "${text}"`);

        if (!this.isInitialized) {
            console.warn('Voice service not initialized - attempting to initialize');
            try {
                await this.initialize();
            } catch (error) {
                console.error('Failed to initialize voice service for TTS test:', error);
                throw error;
            }
        }

        // Mark user gesture for TTS
        this.markUserGesture();

        return new Promise((resolve, reject) => {
            // Enhanced mobile/tablet support with simple, direct approach
            if (this.isMobile) {
                console.log('📱 Using direct tablet TTS approach for test');
                
                // Check if speech synthesis is available and ready
                if (!speechSynthesis || !speechSynthesis.speak) {
                    console.error('❌ Speech synthesis not available on this mobile device');
                    reject(new Error('Speech synthesis not available on this mobile device'));
                    return;
                }
                
                // Pre-check for Hebrew voices
                const voices = speechSynthesis.getVoices();
                const hasHebrewVoice = voices.some(voice => 
                    voice.lang.includes('he') || voice.lang.includes('iw') || voice.name.includes('Hebrew')
                );
                
                if (!hasHebrewVoice && voices.length > 0) {
                    console.warn('⚠️ No Hebrew voice available on this Android tablet');
                    // Still proceed with test using fallback voice
                }
                
                // Wait for voices to load on mobile devices
                this.waitForVoicesAndSpeak(text, resolve, reject);
            } else {
                // Desktop TTS
                this.performTTS(text, resolve);
            }
        });
    }

    // Wait for voices to load before speaking (critical for Android tablets)
    private waitForVoicesAndSpeak(text: string, resolve: () => void, reject: (error: Error) => void): void {
        let attempts = 0;
        const maxAttempts = 20; // Maximum 10 seconds of waiting
        let voicesCheckExecuted = false; // Prevent double execution
        
        const checkVoices = () => {
            // Prevent double execution of this function
            if (voicesCheckExecuted) {
                console.log('📱 Voices already being processed, skipping duplicate check');
                return;
            }
            
            attempts++;
            const voices = speechSynthesis.getVoices();
            console.log(`📱 Voice check attempt ${attempts}/${maxAttempts}, voices count: ${voices.length}`);
            
            if (voices.length > 0) {
                voicesCheckExecuted = true; // Mark as executed
                console.log('📱 Voices loaded! Available voices:');
                voices.slice(0, 5).forEach(voice => {
                    console.log(`  - ${voice.name} (${voice.lang})`);
                });
                console.log('📱 Proceeding with TTS...');
                this.performSimpleTabletTTS(text, resolve);
            } else if (attempts >= maxAttempts) {
                voicesCheckExecuted = true; // Mark as executed
                console.error('📱 Timeout waiting for voices to load, proceeding anyway');
                this.performSimpleTabletTTS(text, resolve);
            } else {
                console.log('📱 Still waiting for voices, retrying in 500ms...');
                setTimeout(checkVoices, 500);
            }
        };

        // Android tablets often need voices to load first
        if (speechSynthesis.getVoices().length === 0) {
            console.log('📱 No voices available yet, setting up voice loading...');
            
            // Listen for voices changed event
            speechSynthesis.onvoiceschanged = () => {
                console.log('📱 Voices changed event fired');
                speechSynthesis.onvoiceschanged = null; // Remove listener
                checkVoices();
            };
            
            // Also start polling for voices as a fallback (with delay to let event handler run first)
            setTimeout(checkVoices, 1000);
        } else {
            console.log('📱 Voices already available, checking immediately');
            checkVoices();
        }
    }

    stopListening(): void {
        if (this.recognition) {
            try {
                console.log('🛑 Stopping speech recognition...');
                this.recognition.abort(); // Use abort() for immediate stop
                this.isListening = false;
                console.log('✅ Speech recognition stopped');
            } catch (error) {
                console.warn('Error stopping speech recognition:', error);
                this.isListening = false;
            }
        }
    }

    private async performWarmupRecognition(): Promise<void> {
        console.log('🔥 Starting iOS warm-up recognition session');
        
        return new Promise<void>((resolve, reject) => {
            const warmupRecognition = new ((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition)();
            
            // Quick warm-up configuration
            warmupRecognition.continuous = false;
            warmupRecognition.interimResults = false;
            warmupRecognition.lang = 'he-IL';
            warmupRecognition.maxAlternatives = 1;
            
            let warmupCompleted = false;
            const warmupTimeout = setTimeout(() => {
                if (!warmupCompleted) {
                    warmupCompleted = true;
                    console.log('⏰ iOS warm-up timeout - completing');
                    warmupRecognition.stop();
                    resolve();
                }
            }, 1500); // Quick 1.5s warm-up
            
            warmupRecognition.onstart = () => {
                console.log('🎤 iOS warm-up recognition started');
            };
            
            warmupRecognition.onresult = () => {
                if (!warmupCompleted) {
                    warmupCompleted = true;
                    console.log('✅ iOS warm-up recognition got result - completing');
                    clearTimeout(warmupTimeout);
                    warmupRecognition.stop();
                    resolve();
                }
            };
            
            warmupRecognition.onerror = (event: any) => {
                if (!warmupCompleted) {
                    warmupCompleted = true;
                    console.log('⚠️ iOS warm-up recognition error, continuing:', event.error);
                    clearTimeout(warmupTimeout);
                    resolve(); // Don't reject, just continue
                }
            };
            
            warmupRecognition.onend = () => {
                if (!warmupCompleted) {
                    warmupCompleted = true;
                    console.log('🏁 iOS warm-up recognition ended');
                    clearTimeout(warmupTimeout);
                    resolve();
                }
            };
            
            try {
                warmupRecognition.start();
            } catch (error) {
                warmupCompleted = true;
                clearTimeout(warmupTimeout);
                console.log('❌ iOS warm-up failed to start:', error);
                resolve(); // Don't reject, just continue
            }
        });
    }

    // TEMPORARY: Method to re-enable TTS for testing
    enableTTS(): void {
        this.disableTTS = false;
        console.log('?? TTS re-enabled');
    }

    // TEMPORARY: Method to disable TTS for testing
    disableTTSMode(): void {
        this.disableTTS = true;
        console.log('?? TTS disabled');
    }
}